import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class CrossSellComponent extends HTMLElement {
  constructor() {
    super();

    this.selects = null;
    this.form = null;
    this.formInputs = [];
    this.submitButton = null;
    this.isInCart = false;
    this.isInASlider = false;
    this.productHandle = null;
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('[data-ref="variant-select"]');
    this.form = this.querySelector('[data-ref="add-to-cart-form"]');
    this.formInputs = this.querySelectorAll('[data-ref="product-input"]');
    this.submitButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.isInCart = this.closest('cart-drawer') || this.closest('cart-component') ? true : false;
    this.isInASlider = this.closest('slider-component') ? true : false;
    this.productHandle = this.dataset.productHandle;

    this.submitButton.addEventListener('click', this.#handleSubmit);

    this.selects.forEach((select) => {
      select.addEventListener('change', this.#handleSelectChange);
    });

    if (this.isInCart) this.#synchronizeWithCartContent();
  }

  disconnectedCallback() {
    this.submitButton.removeEventListener('click', this.#handleSubmit);

    this.selects.forEach((select) => {
      select.removeEventListener('change', this.#handleSelectChange);
    });
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#shouldHideAddedItems() && this.submitButton.getAttribute('aria-pressed') === 'true') {
      this.submitButton.classList.add('is-loading');
      await this.#removeHiddenCartItems();
      return;
    }

    this.submitButton.classList.add('is-loading');
    this.#setSwitchActive(true);

    const items = [];

    this.selects.forEach((select) => {
      const item = {
        id: select.value,
        quantity: 1,
      };

      if (this.#shouldHideAddedItems()) {
        item.properties = {
          _cart_drawer_cross_sell: 'true',
        };
      }

      items.push(item);
    });

    const sections = this.#getSections();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
        }),
      });

      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      const data = await response.json();

      this.updateCart(data, items.length);

      this.submitButton.classList.remove('is-loading');
    } catch (error) {
      console.error('Error:', error);
      this.#setSwitchActive(false);
      this.submitButton.classList.remove('is-loading');
    }
  };

  #setSwitchActive(active) {
    if (!this.submitButton.classList.contains('cross-sell__switch-button')) return;

    this.submitButton.classList.toggle('button--active', active);
    this.submitButton.setAttribute('aria-pressed', String(active));
  }

  #shouldHideAddedItems() {
    return this.submitButton.classList.contains('cross-sell__switch-button') && Boolean(this.closest('cart-drawer'));
  }

  #getSections() {
    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    return sections;
  }

  async #removeHiddenCartItems() {
    try {
      const selectedVariantIds = [...this.selects].map((select) => String(select.value));
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);

      if (!cartResponse.ok) throw new Error(`Cart fetch failed: ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const hiddenLines = cart.items
        .map((item, index) => ({ ...item, line: index + 1 }))
        .filter((item) => selectedVariantIds.includes(String(item.variant_id)) && item.properties?._cart_drawer_cross_sell === 'true')
        .sort((a, b) => b.line - a.line);

      let updatedCart = cart;

      for (const item of hiddenLines) {
        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: item.line,
            quantity: 0,
            sections: this.#getSections().join(','),
          }),
        });

        if (!response.ok) throw new Error(`Cart remove failed: ${response.status}`);

        updatedCart = await response.json();
      }

      this.#setSwitchActive(false);
      this.updateCart(updatedCart, 0);
      this.submitButton.classList.remove('is-loading');
    } catch (error) {
      console.error('Error:', error);
      this.submitButton.classList.remove('is-loading');
    }
  }

  #handleSelectChange = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.submitButton.disabled = true;
    this.updateBundlePrices();

    this.submitButton.disabled = false;

    if (this.dataset.dynamicVariantImage === 'true') {
      this.updateImage();
    }
  };

  updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (data.item_count != null) {
        newItemCount = data.item_count;
      } else if (currentItemCount != null) {
        newItemCount = currentItemCount + itemsAdded;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'cross-sell',
            sections: data.sections,
          },
        ),
      );
    }
  }

  updateBundlePrices() {
    if (this.dataset.customPrice === 'true') return;

    const priceContainer = this.querySelector('.price-container .price');
    const compareAtPriceContainer = this.querySelector('.price-container .compare-at-price');

    let price = 0;
    let compareAtPrice = 0;

    this.selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];

      if (selectedOption != null) {
        price += parseInt(selectedOption.dataset.price);
        compareAtPrice += parseInt(selectedOption.dataset.compareAtPrice) || parseInt(selectedOption.dataset.price);
      } else {
        price += parseInt(select.dataset.price);
        compareAtPrice += parseInt(select.dataset.compareAtPrice) || parseInt(select.dataset.price);
      }
    });

    priceContainer.innerText = formatMoney(price);
    if (compareAtPrice > price) {
      compareAtPriceContainer.innerText = formatMoney(compareAtPrice);
    } else {
      compareAtPriceContainer.innerText = '';
    }
  }

  updateImage() {
    const image = this.querySelector('[data-ref="cross-sell-image"]');
    if (!image) return;

    const selectedOption = this.selects[0].options[this.selects[0].selectedIndex];
    if (!selectedOption.dataset.image) return;

    image.src = selectedOption.dataset.image;
  }

  // Si le cross-sell est dans un panier, et que le produit est dans le panier, on masque le cross-sell
  #synchronizeWithCartContent() {
    let cartItems = null;
    if (this.closest('cart-drawer')) cartItems = this.closest('cart-drawer').querySelectorAll('[data-ref="cart-item"]');
    if (this.closest('cart-component')) cartItems = this.closest('cart-component').querySelectorAll('[data-ref="cart-item"]');

    if (!cartItems) return;

    const closestSlider = this.closest('slider-component');

    cartItems.forEach((cartItem) => {
      if (cartItem.dataset.productHandle === this.productHandle) {
        if (this.isInASlider) {
          closestSlider.querySelectorAll('.splide__slide').forEach((slide, index) => {
            if (slide == this.closest('.splide__slide')) {
              closestSlider.remove(index);
            }
          });
        } else {
          this.classList.add('hidden');
        }
      }
    });
  }
}

if (!customElements.get('cross-sell-component')) {
  customElements.define('cross-sell-component', CrossSellComponent);
}
